// Login page — posts to /api/v1/auth/login (BE-003) and then lets the
// FE-A AuthProvider load /api/v1/auth/me before redirecting to the
// dashboard. The API client's cookie + X-Entity-Id wiring takes
// over once the auth context has the active membership.

import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { AuthShell, useAuth } from "@/features/auth";


export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Already-authenticated visitors should skip the form entirely.
  // useEffect fires the redirect; the early-return (below all hook
  // calls — required by the rules of hooks) keeps the login form
  // from rendering during the same render.
  useEffect(() => {
    if (auth.status === "authenticated") {
      const fromState = (location.state as { from?: string } | null)?.from;
      navigate(fromState ?? "/dashboard", { replace: true });
    }
  }, [auth.status, location.state, navigate]);

  const mutation = useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      auth.login(body),
    onSuccess: () => {
      const fromState = (location.state as { from?: string } | null)?.from;
      navigate(fromState ?? "/dashboard", { replace: true });
    },
  });

  if (auth.status === "authenticated") {
    return null;
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate({ email, password });
  }

  // Surface an approachable message even when the API returns a raw
  // error class — never leak technical detail like ``ApiError 401``.
  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      // The BE-003 contract returns ``invalid_credentials`` for bad
      // email/password; treat that as the friendly default and pass
      // anything else through verbatim.
      if (
        mutation.error.status === 401
        || /invalid/i.test(mutation.error.message)
      ) {
        return "That email and password combination didn't match. Try again.";
      }
      return mutation.error.message;
    }
    return "Something went wrong signing you in. Try again in a moment.";
  })();

  return (
    <AuthShell layout="centered" testId="login-page">
      <header className="flex flex-col gap-1">
        <h1
          data-testid="login-title"
          className="text-xl font-semibold tracking-tight text-text-primary"
        >
          Welcome back
        </h1>
        <p className="text-sm text-text-secondary">
          Sign in with your brokerage credentials.
        </p>
      </header>

      <form
        className="mt-6 flex flex-col gap-4"
        onSubmit={onSubmit}
        data-testid="login-form"
        noValidate
      >
        <label className="block">
          <span className="block text-sm font-medium text-text-primary">
            Email
          </span>
          <input
            type="email"
            autoComplete="email"
            required
            data-testid="login-email"
            placeholder="you@brokerage.com"
            className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out placeholder:text-text-tertiary focus:border-teal focus:shadow-focus"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-text-primary">
            Password
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            data-testid="login-password"
            className="mt-1 w-full rounded-md border border-border-default bg-raised px-3 py-2 text-base text-text-primary outline-none transition-fast ease-out focus:border-teal focus:shadow-focus"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {errorMessage && (
          <div
            role="alert"
            data-testid="login-error"
            className="rounded-md border border-ember/20 bg-ember-tint px-3 py-2 text-sm text-ember"
          >
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          data-testid="login-submit"
          className="mt-2 w-full rounded-md bg-teal px-4 py-2.5 text-base font-medium text-text-inverse transition-base ease-out hover:opacity-90 focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-50"
        >
          {mutation.isPending ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <p className="mt-6 border-t border-border-subtle pt-4 text-xs text-text-tertiary">
        New here?{" "}
        <Link
          to="/signup"
          data-testid="login-signup-link"
          className="font-medium text-teal hover:underline"
        >
          Create an account
        </Link>
        . Already on a team and need access? Ask your brokerage administrator
        to send you an invitation.
      </p>
    </AuthShell>
  );
}
